import React, { useMemo, useRef, useEffect } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import './ChartComponent.scss';

ChartJS.register(...registerables);

const ChartComponent = ({timeStampData}) => {
  // Use liveWPMData if available for smoother graph, fallback to wordsOverTime
  const { wpmChartData, xMax, yMax } = useMemo(() => {
    let localXMax = -Infinity;
    let localYMax = -Infinity;
    let data = [];
    
    if (timeStampData.liveWPMData && timeStampData.liveWPMData.length > 0) {
      // Use live WPM data for smoother graph
      data = timeStampData.liveWPMData.map(item => ({
        x: item.t, // Already in seconds from start
        y: item.wpm
      }));
      // Calculate max values
      data.forEach(item => {
        localXMax = Math.max(localXMax, item.x);
        localYMax = Math.max(localYMax, item.y);
      });
    } else {
      // Fallback to word-based data
      let myData = [];
      for (let key in timeStampData.wordsOverTime) {
        myData.push({
          timestamp: timeStampData.wordsOverTime[key][0],
          wpm: timeStampData.wordsOverTime[key][1]
        });
        localXMax = Math.max(localXMax, timeStampData.wordsOverTime[key][0]);
        localYMax = Math.max(localYMax, timeStampData.wordsOverTime[key][1]);
      }
      data = myData.map(item => ({
        x: item.timestamp,
        y: item.wpm
      }));
    }
    
    return { wpmChartData: data, xMax: localXMax, yMax: localYMax };
  }, [timeStampData]);
  
  // Process incorrect words data - deduplicate by word and use first error timestamp
  // Structure: incorrectWords[wordIndex] = [[incorrectChar, timestamp, wpm], ...]
  const incorrectWPMChartData = useMemo(() => {
    const incorrectData = [];
    const seenWords = new Set(); // Track words we've already added to prevent duplicates
    
    if (!timeStampData || !timeStampData.incorrectWords) {
      console.log('[ChartComponent] No incorrectWords data:', { timeStampData, hasIncorrectWords: !!timeStampData?.incorrectWords });
      return incorrectData;
    }
    
    console.log('[ChartComponent] Processing incorrectWords:', timeStampData.incorrectWords);
    
    for(let wordIndex in timeStampData.incorrectWords) {
      // Only process if we haven't seen this word index before
      if (!seenWords.has(wordIndex) && timeStampData.incorrectWords[wordIndex] && timeStampData.incorrectWords[wordIndex].length > 0) {
        // Use the first error for each word (first incorrect character)
        const firstError = timeStampData.incorrectWords[wordIndex][0];
        // Format: [incorrectChar, timestamp (seconds), wpm]
        if (firstError && Array.isArray(firstError) && firstError.length >= 3) {
          const timestamp = firstError[1];
          const wpm = firstError[2];
          
          console.log('[ChartComponent] Processing error:', { wordIndex, firstError, timestamp, wpm, isValid: typeof timestamp === 'number' && !isNaN(timestamp) && typeof wpm === 'number' && !isNaN(wpm) });
          
          // Validate data before adding
          if (typeof timestamp === 'number' && !isNaN(timestamp) && typeof wpm === 'number' && !isNaN(wpm)) {
            incorrectData.push({
              x: timestamp, // timestamp in seconds (already aligned with x-axis)
              y: wpm  // wpm at time of error
            });
            seenWords.add(wordIndex);
          }
        }
      }
    }
    
    console.log('[ChartComponent] Final incorrectData:', incorrectData);
    return incorrectData;
  }, [timeStampData]);    
  const labels1 = useMemo(()=>([]),[]);
  for(let i = 0; i <= 45; i++){
    labels1.push(i);
  }
  const data1 = useMemo(()=>({
    labels: labels1,
    datasets: [{
        type: 'line',
        label: 'Words Per Minute',
        data: wpmChartData,
        backgroundColor: 'rgba(77, 208, 225, 0.2)',
        borderColor: '#4dd0e1',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: '#4dd0e1',
        pointBorderColor: '#4dd0e1',
        pointHoverBackgroundColor: '#b2ebf2',
        pointHoverBorderColor: '#4dd0e1',
        tension: 0.4,
        fill: true,
      },
      {
        type: 'scatter',
        label: 'Incorrect words',
        backgroundColor: 'rgba(255, 77, 148, 0.6)',
        borderColor: '#ff4d94',
        pointRadius: 6,
        pointHoverRadius: 10,
        pointBackgroundColor: '#ff4d94',
        pointBorderColor: '#ff4d94',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#ff80b3',
        pointHoverBorderColor: '#ff4d94',
        showLine: false,
        data: incorrectWPMChartData
      }
    ],
  }), [incorrectWPMChartData, labels1, wpmChartData]);

  // Calculate max x value including incorrect words timestamps
  const maxXValue = useMemo(() => {
    let max = xMax;
    if (incorrectWPMChartData.length > 0) {
      const maxIncorrect = Math.max(...incorrectWPMChartData.map(item => item.x));
      max = Math.max(max, maxIncorrect);
    }
    return Math.round(max + 2);
  }, [xMax, incorrectWPMChartData]);
  
  const chartOptions = useMemo(()=> ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        labels: {
          color: '#4dd0e1',
          font: {
            family: 'Merriweather',
            size: 14,
            weight: 'bold'
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        },
        display: true,
        position: 'top'
      },
      tooltip: {
        backgroundColor: 'rgba(10, 10, 15, 0.9)',
        titleColor: '#4dd0e1',
        bodyColor: '#b2ebf2',
        borderColor: '#4dd0e1',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxPadding: 6,
        titleFont: {
          family: 'Merriweather',
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: 'Merriweather',
          size: 12
        },
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Incorrect words') {
              return `Error at ${context.parsed.x}s: ${context.parsed.y} WPM`;
            }
            return `${context.dataset.label}: ${context.parsed.y} WPM`;
          }
        }
      }
    },
    scales: {      
      x: {
        title: {
          display: true,
          text: 'Time (Seconds)',
          color: '#4dd0e1',
          font: {
            family: 'Merriweather',
            size: 16,
            weight: 'bold'
          },
          padding: { top: 10, bottom: 10 }
        },
        min: 0,
        max: maxXValue,
        ticks: {
          color: '#4dd0e1',
          font: {
            family: 'Merriweather',
            size: 12
          },
          stepSize: 2
        },
        grid: {
          color: 'rgba(77, 208, 225, 0.2)',
          lineWidth: 1,
          drawBorder: true,
          borderColor: '#4dd0e1',
          borderWidth: 2
        }
      },
      y: {
        title: {
          display: true,
          text: 'WPM (Words Per Minute)',
          color: '#4dd0e1',
          font: {
            family: 'Merriweather',
            size: 16,
            weight: 'bold'
          },
          padding: { top: 10, bottom: 10 }
        },
        min: 0,
        max: Math.round(yMax+10),
        ticks: {
          color: '#4dd0e1',
          font: {
            family: 'Merriweather',
            size: 12
          },
          stepSize: 10
        },
        grid: {
          color: 'rgba(77, 208, 225, 0.2)',
          lineWidth: 1,
          drawBorder: true,
          borderColor: '#4dd0e1',
          borderWidth: 2
        }
      },
    }
  }),[maxXValue, yMax])
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);  
  
  // Initialize and update chart
  useEffect(() => {    
    if (chartRef.current) {
      if (!chartInstanceRef.current) {
        // Create chart on first render
        chartInstanceRef.current = new ChartJS(chartRef.current, {
          type: 'scatter',
          data: data1,
          options: chartOptions,
        });
      } else {
        // Update existing chart when data changes
        chartInstanceRef.current.data = data1;
        chartInstanceRef.current.options = chartOptions;
        chartInstanceRef.current.update('none'); // 'none' mode for smoother updates
      }
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [data1, chartOptions]);

  return (
    <div className="chart-component">                  
      <h2 className="chart-component__title">Words Per Minute Over Time</h2>
      <div className="chart-component__canvas">
        <canvas ref={chartRef} />
      </div>
    </div>    
  );
};

export default ChartComponent;
